/**
 * 매뉴얼 전문 검색 인덱스 (순수 로직 — DOM·React 의존 없음).
 *
 * 문서가 수십 편으로 늘면 목차로는 못 찾는다. 검색이 실제 진입로가 되므로,
 * **한글에서 실제로 동작하는** 매칭이 이 모듈의 전부다.
 *
 * ## 왜 2-gram인가
 * 한글은 형태소 경계가 공백과 일치하지 않는다("값 연결"에서 "연결"만 떼어내는 토큰화가
 * 공백 분리로는 안 된다). 영어식 단어 토큰 분리·edit distance fuzzy는 한글 질의에서
 * 사실상 무용지물이라, 정규화한 문자열의 **연속 2글자(바이그램)** 를 인덱싱한다.
 * "값 연결"은 "값연/연결"을 갖고, 질의 "연결"·"값연결" 모두 여기에 걸린다.
 *
 * ## 정규화 = 소문자 + 공백 제거
 * 공백을 지운 문자열에서 부분 문자열을 찾으므로 "값연결" ↔ "값 연결" 띄어쓰기 차이가
 * 자동으로 흡수된다. 지운 공백 때문에 원문 위치가 어긋나므로, 필드마다
 * `정규화 인덱스 → 원문 인덱스` 대응표(`map`)를 함께 들고 다니며 스니펫·하이라이트를 원문 기준으로 낸다.
 *
 * ## 인덱싱 대상
 * `title`(최상) > `summary` > 본문 제목(`##`/`###`) > 본문 텍스트.
 * 본문은 프론트매터·코드펜스·마크다운 기호를 걷어낸 **읽을 수 있는 문장**만 넣는다
 * (스니펫에 `|`·`##`·링크 문법이 새면 결과가 읽히지 않는다).
 */
import { parseFrontmatter, type ManualDoc } from './manualParser';

/** 결과 스니펫 안 하이라이트 구간 (스니펫 문자열 기준 오프셋). */
export interface SearchHighlight {
  start: number;
  length: number;
}

/** 어느 필드에서 걸렸는지 — 정렬 근거이자 결과 UI의 배지. */
export type SearchFieldKind = 'title' | 'summary' | 'heading' | 'body';

export interface ManualSearchResult {
  doc: ManualDoc;
  /** `manual/section/slug` — 결과 목록의 key. */
  key: string;
  manual: string;
  section: string;
  slug: string;
  title: string;
  /** 매칭이 가장 강했던 필드. */
  field: SearchFieldKind;
  /** 본문·본문 제목에서 걸렸을 때 그 위치를 덮는 제목 (없으면 undefined). */
  heading?: string;
  /** 매칭 주변 문맥 (앞뒤가 잘리면 `…`이 붙는다). */
  snippet: string;
  /** 스니펫 안 매칭 위치. */
  highlights: SearchHighlight[];
  score: number;
}

export interface ManualSearchIndex {
  docs: IndexedDoc[];
  /** 바이그램 → 그 바이그램을 가진 문서 인덱스 (후보 좁히기 전용). */
  byBigram: Map<string, number[]>;
}

/** 필드 종류별 가중치 — 제목 매칭이 본문 매칭보다 항상 위로 오게 하는 유일한 장치. */
const FIELD_WEIGHT: Record<SearchFieldKind, number> = {
  title: 100,
  summary: 45,
  heading: 25,
  body: 10,
};

/** 부분(바이그램) 매칭으로 결과에 넣어줄 최소 커버리지 — 이 아래는 소음이라 버린다. */
const PARTIAL_MIN_COVERAGE = 0.6;

/** 스니펫에서 매칭 앞뒤로 남길 문자 수. */
const SNIPPET_BEFORE = 30;
const SNIPPET_AFTER = 70;

/** 한 결과에 표시할 하이라이트 최대 개수. */
const MAX_HIGHLIGHTS = 8;

export interface IndexedField {
  kind: SearchFieldKind;
  /** 마크다운을 걷어낸 원문 (스니펫 출처). */
  text: string;
  /** 소문자 + 공백 제거. 매칭은 전부 이 문자열 위에서 일어난다. */
  norm: string;
  /** `norm[i]`가 원래 `text`의 몇 번째 글자였는지. */
  map: number[];
  /** `norm`의 연속 2글자 집합. */
  bigrams: Set<string>;
  /** 이 필드를 덮는 본문 제목 (title/summary는 없음). */
  heading?: string;
}

export interface IndexedDoc {
  doc: ManualDoc;
  fields: IndexedField[];
}

/* ------------------------------------------------------------------ 정규화 */

/**
 * 소문자 + 공백 제거 문자열과 원문 위치 대응표를 만든다.
 *
 * 대응표가 없으면 스니펫이 지워진 공백 수만큼 밀린다(한 문장에 공백이 여럿이면 눈에 띄게 어긋남).
 */
function normalizeWithMap(text: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (/\s/.test(ch)) continue;
    norm += ch.toLowerCase();
    map.push(i);
  }
  return { norm, map };
}

/** 질의 정규화 — 본문과 같은 규칙(소문자 + 공백 제거)이어야 띄어쓰기 차이가 흡수된다. */
export function normalizeQuery(query: string): string {
  return query.replace(/\s+/g, '').toLowerCase();
}

function bigramsOf(norm: string): string[] {
  const out: string[] = [];
  for (let i = 0; i + 1 < norm.length; i += 1) out.push(norm.slice(i, i + 2));
  return out;
}

/* ------------------------------------------------------- 마크다운 → 평문 */

/** 한 줄에서 마크다운 기호를 걷어낸다 (스니펫에 문법이 새지 않게). */
function stripInlineMarkdown(line: string): string {
  return (
    line
      // 이미지는 alt까지 버린다 (문장이 아니다)
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      // 링크는 표시 텍스트만 남긴다
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // 자동 링크 `<https://…>`
      .replace(/<(https?:\/\/[^>]+)>/g, '$1')
      // HTML 태그·주석
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<\/?[A-Za-z][^>]*>/g, '')
      // 코드 스팬은 내용이 곧 검색어다 (`rangeMap` 등) — 백틱만 벗긴다
      .replace(/`([^`]*)`/g, '$1')
      // 강조 (`_`는 snake_case 식별자를 깨뜨리므로 건드리지 않는다)
      .replace(/\*\*?/g, '')
      .replace(/~~/g, '')
      // 표 구분자 — 셀 경계는 공백으로
      .replace(/\|/g, ' ')
      // 인용·콜아웃 마커
      .replace(/^[ \t]*>[ \t]?/, '')
      .replace(/^\[!(TIP|WARNING|TRAP)\][ \t]*/i, '')
      // 목록·번호 마커
      .replace(/^[ \t]*([-*+]|\d+\.)[ \t]+/, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/** 표 구분 줄(`|---|:--:|`)처럼 읽을 내용이 없는 줄. */
function isNoiseLine(line: string): boolean {
  const t = line.trim();
  if (!t) return true;
  if (/^[|\s:-]+$/.test(t) && t.includes('-')) return true;
  return /^(-{3,}|\*{3,}|_{3,})$/.test(t);
}

export interface BodyChunk {
  heading?: string;
  text: string;
}

/**
 * 본문 마크다운을 **제목 단위 평문 덩어리**로 자른다.
 *
 * 덩어리를 제목으로 나눠 두면 본문 매칭에 "어느 절 이야기인지"가 공짜로 따라온다.
 * 코드펜스는 통째로 버린다 — 검색되면 스니펫이 코드 조각이 되어 읽히지 않는다.
 */
export function toSearchableBody(body: string): { headings: string[]; chunks: BodyChunk[] } {
  // 문서 본문은 이미 프론트매터가 제거돼 있지만, 원문을 그대로 넘겨도 안전하게.
  const source = parseFrontmatter(body).body;
  const headings: string[] = [];
  const chunks: BodyChunk[] = [];

  let currentHeading: string | undefined;
  let buffer: string[] = [];
  let inFence = false;

  const flush = () => {
    const text = buffer.join(' ').replace(/\s+/g, ' ').trim();
    if (text) chunks.push({ heading: currentHeading, text });
    buffer = [];
  };

  for (const rawLine of source.split(/\r?\n/)) {
    if (/^[ \t]*(```|~~~)/.test(rawLine)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const heading = /^[ \t]*(#{1,6})[ \t]+(.*)$/.exec(rawLine);
    if (heading) {
      flush();
      const text = stripInlineMarkdown(heading[2]);
      currentHeading = text || undefined;
      if (text) headings.push(text);
      continue;
    }

    if (isNoiseLine(rawLine)) continue;
    const cleaned = stripInlineMarkdown(rawLine);
    if (cleaned) buffer.push(cleaned);
  }
  flush();

  return { headings, chunks };
}

/* ------------------------------------------------------------------- 인덱스 */

function makeField(
  kind: SearchFieldKind,
  text: string,
  heading?: string,
): IndexedField | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const { norm, map } = normalizeWithMap(trimmed);
  if (!norm) return null;
  return { kind, text: trimmed, norm, map, bigrams: new Set(bigramsOf(norm)), heading };
}

/**
 * 문서 배열에서 검색 인덱스를 만든다.
 *
 * 호출부는 모듈 최초 로드 시 **한 번만** 부르고 결과를 재사용해야 한다
 * (질의마다 다시 만들면 본문 파싱이 매 타자마다 돈다).
 */
export function buildSearchIndex(docs: ManualDoc[]): ManualSearchIndex {
  const indexed: IndexedDoc[] = [];
  const byBigram = new Map<string, number[]>();

  for (const doc of docs) {
    const fields: IndexedField[] = [];
    const title = makeField('title', doc.title);
    if (title) fields.push(title);
    const summary = makeField('summary', doc.summary);
    if (summary) fields.push(summary);

    const { headings, chunks } = toSearchableBody(doc.body);
    for (const heading of headings) {
      const field = makeField('heading', heading, heading);
      if (field) fields.push(field);
    }
    for (const chunk of chunks) {
      const field = makeField('body', chunk.text, chunk.heading);
      if (field) fields.push(field);
    }

    if (fields.length === 0) continue;

    const docIndex = indexed.length;
    indexed.push({ doc, fields });

    const seen = new Set<string>();
    for (const field of fields) {
      for (const bigram of field.bigrams) {
        if (seen.has(bigram)) continue;
        seen.add(bigram);
        const list = byBigram.get(bigram);
        if (list) list.push(docIndex);
        else byBigram.set(bigram, [docIndex]);
      }
    }
  }

  return { docs: indexed, byBigram };
}

/* -------------------------------------------------------------------- 검색 */

interface FieldMatch {
  field: IndexedField;
  score: number;
  /** 원문(`field.text`) 기준 매칭 구간들. 부분 매칭이면 비어 있을 수 있다. */
  ranges: SearchHighlight[];
}

/** `norm` 인덱스 구간을 원문 구간으로 되돌린다. */
function toTextRange(field: IndexedField, normStart: number, normLength: number): SearchHighlight {
  const start = field.map[normStart];
  const lastNorm = Math.min(normStart + normLength - 1, field.map.length - 1);
  const end = field.map[lastNorm];
  return { start, length: end - start + 1 };
}

/** 한 필드 안에서 질의를 찾는다 — 완전 일치 우선, 없으면 바이그램 커버리지. */
function matchField(field: IndexedField, qNorm: string, qBigrams: string[]): FieldMatch | null {
  const weight = FIELD_WEIGHT[field.kind];
  const ranges: SearchHighlight[] = [];

  let at = field.norm.indexOf(qNorm);
  if (at >= 0) {
    let hits = 0;
    while (at >= 0 && ranges.length < MAX_HIGHLIGHTS) {
      ranges.push(toTextRange(field, at, qNorm.length));
      hits += 1;
      at = field.norm.indexOf(qNorm, at + Math.max(1, qNorm.length));
    }
    const first = ranges[0].start;
    // 앞머리 일치는 "그걸 찾은 것"에 가깝고, 필드 전체가 질의면 더 강하다.
    const prefixBonus = first === 0 ? 0.5 : 0;
    const exactBonus = field.norm === qNorm ? 1 : 0;
    // 같은 필드에 여러 번 나오면 소폭 가산 (지배하지 않도록 로그 대용으로 얕게).
    const repeatBonus = Math.min(hits - 1, 3) * 0.05;
    return { field, score: weight * (1 + prefixBonus + exactBonus + repeatBonus), ranges };
  }

  if (qBigrams.length === 0) return null;

  let covered = 0;
  let firstBigramAt = -1;
  for (const bigram of qBigrams) {
    if (!field.bigrams.has(bigram)) continue;
    covered += 1;
    if (firstBigramAt < 0) firstBigramAt = field.norm.indexOf(bigram);
  }
  const coverage = covered / qBigrams.length;
  if (coverage < PARTIAL_MIN_COVERAGE) return null;

  // 부분 매칭은 완전 일치보다 확실히 아래에 놓는다(가중치 0.4배).
  const score = weight * coverage * 0.4;
  const ranges2 =
    firstBigramAt >= 0 ? [toTextRange(field, firstBigramAt, 2)] : [];
  return { field, score, ranges: ranges2 };
}

/** 매칭 주변을 잘라 문맥 스니펫을 만든다 (하이라이트 오프셋도 함께 옮긴다). */
function buildSnippet(field: IndexedField, ranges: SearchHighlight[]): {
  snippet: string;
  highlights: SearchHighlight[];
} {
  const text = field.text;
  if (ranges.length === 0) {
    const snippet = text.length > SNIPPET_AFTER + SNIPPET_BEFORE
      ? `${text.slice(0, SNIPPET_AFTER + SNIPPET_BEFORE)}…`
      : text;
    return { snippet, highlights: [] };
  }

  const first = ranges[0];
  const from = Math.max(0, first.start - SNIPPET_BEFORE);
  const to = Math.min(text.length, first.start + first.length + SNIPPET_AFTER);
  const prefix = from > 0 ? '…' : '';
  const suffix = to < text.length ? '…' : '';
  const body = text.slice(from, to);
  const shift = prefix.length - from;

  const highlights = ranges
    .filter((r) => r.start >= from && r.start + r.length <= to)
    .map((r) => ({ start: r.start + shift, length: r.length }));

  return { snippet: `${prefix}${body}${suffix}`, highlights };
}

/**
 * 인덱스에서 질의를 검색한다.
 *
 * - 빈 질의·공백뿐인 질의는 **빈 배열**(호출부가 "입력하세요" 상태를 그린다)
 * - 매칭이 없어도 빈 배열 — throw 없음
 * - 한 문서는 가장 강하게 걸린 필드 하나로 대표된다 (같은 문서가 여러 줄로 중복되지 않게)
 */
export function searchManual(
  index: ManualSearchIndex,
  query: string,
  limit = 20,
): ManualSearchResult[] {
  const qNorm = normalizeQuery(query);
  if (!qNorm) return [];

  const qBigrams = bigramsOf(qNorm);

  // 후보 좁히기: 바이그램이 하나도 안 걸리는 문서는 볼 필요가 없다.
  // 한 글자 질의는 바이그램이 없으므로 전수 검사(문서 수십 편 규모라 부담 없음).
  let candidates: number[];
  if (qBigrams.length === 0) {
    candidates = index.docs.map((_, i) => i);
  } else {
    const hit = new Set<number>();
    for (const bigram of qBigrams) {
      for (const docIndex of index.byBigram.get(bigram) ?? []) hit.add(docIndex);
    }
    candidates = [...hit];
  }

  const results: ManualSearchResult[] = [];

  for (const docIndex of candidates) {
    const entry = index.docs[docIndex];
    let best: FieldMatch | null = null;
    let total = 0;

    for (const field of entry.fields) {
      const match = matchField(field, qNorm, qBigrams);
      if (!match) continue;
      total += match.score;
      if (!best || match.score > best.score) best = match;
    }

    if (!best) continue;

    const { snippet, highlights } = buildSnippet(best.field, best.ranges);
    const doc = entry.doc;
    results.push({
      doc,
      key: `${doc.manual}/${doc.section}/${doc.slug}`,
      manual: doc.manual,
      section: doc.section,
      slug: doc.slug,
      title: doc.title,
      field: best.field.kind,
      heading: best.field.heading,
      snippet,
      highlights,
      // 대표 필드 점수 + 나머지 필드의 얕은 가산(여러 곳에서 걸린 문서를 조금 위로).
      score: best.score + (total - best.score) * 0.1,
    });
  }

  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.doc.order !== b.doc.order) return a.doc.order - b.doc.order;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  return results.slice(0, limit);
}

/**
 * 스니펫을 하이라이트 기준으로 조각낸다 (렌더가 문자열 자르기를 다시 하지 않도록).
 *
 * `dangerouslySetInnerHTML` 없이 `<mark>`를 그리기 위한 것 — 검색어가 그대로 HTML이 되면
 * 문서 본문의 태그·꺾쇠가 주입 경로가 된다.
 */
export function splitHighlights(
  snippet: string,
  highlights: SearchHighlight[],
): { text: string; hit: boolean }[] {
  if (highlights.length === 0) return [{ text: snippet, hit: false }];

  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const parts: { text: string; hit: boolean }[] = [];
  let cursor = 0;

  for (const { start, length } of sorted) {
    if (start < cursor) continue;
    if (start > cursor) parts.push({ text: snippet.slice(cursor, start), hit: false });
    parts.push({ text: snippet.slice(start, start + length), hit: true });
    cursor = start + length;
  }
  if (cursor < snippet.length) parts.push({ text: snippet.slice(cursor), hit: false });

  return parts;
}
